import React, { useState, useEffect, useRef } from "react";
import logo from "../../icon/logo.png";
import supabase from "../../supabase";

/* ─────────────────────────────────────────────
   DESIGN TOKENS — "Digitalbrutalnft // THE RESERVE"
   Matches the landing page: a private vault, not a
   SaaS form. True neutral charcoal black with a single
   brass accent standing in for trust and craft, muted
   garnet/sage for error/success so it reads as considered
   money rather than a generic dashboard login.
───────────────────────────────────────────── */
const C = {
  bg:         "#000000",
  surface:    "#0A0A0A",
  surfaceAlt: "#121212",
  border:     "#1F1F1F",
  borderFocus:"#C9A15C60",
  borderHover:"#333333",
  accent:     "#C9A15C",
  accentDim:  "rgba(201,161,92,0.10)",
  accentGlow: "rgba(201,161,92,0.30)",
  text:       "#EDEDEF",
  textMid:    "#9A9AA0",
  textDim:    "#5C5C62",
  red:        "#C9695F",
  green:      "#6FA383",
  blue:       "#8C99A8",
};

/* ─────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html {
  scroll-behavior: smooth;
  height: 100%;
}

body {
  background: ${C.bg};
  color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

a { text-decoration: none; color: inherit; }

/* ── Animations ── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes gridFade {
  from { opacity: 0; }
  to   { opacity: .3; }
}
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.5; transform:scale(.9); }
}
@keyframes spinDot {
  to { transform: rotate(360deg); }
}
@keyframes shake {
  0%,100% { transform: translateX(0); }
  20%,60% { transform: translateX(-6px); }
  40%,80% { transform: translateX(6px); }
}
@keyframes successPop {
  0%   { transform: scale(.8); opacity:0; }
  60%  { transform: scale(1.08); }
  100% { transform: scale(1); opacity:1; }
}

.auth-card-enter { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) .05s both; }
.auth-header-enter { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) .15s both; }

.grid-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(${C.border} 1px, transparent 1px),
    linear-gradient(90deg, ${C.border} 1px, transparent 1px);
  background-size: 56px 56px;
  animation: gridFade 1.2s ease-out both;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 40%, black 0%, transparent 100%);
}

/* ── Nav ── */
.tf-nav {
  height: 60px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 40px);
  background: ${C.bg}F2; backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid ${C.border};
  position: sticky; top: 0; z-index: 100;
}

/* ── Nav actions / signup CTA ── */
.tf-nav-actions {
  display: flex; align-items: center; gap: 12px;
  min-width: 0;
}
.tf-nav-cta-label {
  font-size: 13px; color: ${C.textDim};
  white-space: nowrap;
}
.tf-nav-cta {
  font-size: 12px; padding: 8px 16px; border-radius: 8px;
  background: ${C.accentDim}; border: 1px solid ${C.accent}40;
  color: ${C.accent}; font-weight: 700; letter-spacing: .01em;
  transition: background .2s, color .2s;
  white-space: nowrap; flex-shrink: 0;
}

@media (max-width: 480px) {
  .tf-nav-actions { gap: 8px; }
  .tf-nav-cta-label { display: none; }
  .tf-nav-cta { font-size: 11px; padding: 7px 11px; }
}
@media (max-width: 360px) {
  .tf-nav-actions { gap: 6px; }
  .tf-nav-cta { font-size: 10px; padding: 6px 9px; }
}

/* ── iOS PWA safe area (notch / Dynamic Island) ── */
@supports (padding-top: env(safe-area-inset-top)) {
  .tf-nav {
    padding-top: env(safe-area-inset-top);
    height: calc(60px + env(safe-area-inset-top));
  }
  .tf-nav-offset {
    min-height: calc(100vh - 60px - env(safe-area-inset-top));
  }
}

/* ── Input ── */
.tf-input {
  width: 100%;
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 14px; color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  outline: none;
  transition: border-color .2s, box-shadow .2s;
}
.tf-input:focus {
  border-color: ${C.accent}60;
  box-shadow: 0 0 0 3px ${C.accentDim};
}
.tf-input::placeholder { color: ${C.textDim}; }
.tf-input.error {
  border-color: ${C.red}70;
  box-shadow: 0 0 0 3px rgba(192,57,43,.08);
}
.tf-input.success {
  border-color: ${C.accent}60;
}

/* ── Password input wrapper ── */
.pw-wrap { position: relative; }
.pw-wrap .tf-input { padding-right: 46px; }
.pw-toggle {
  position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: ${C.textDim}; cursor: pointer;
  font-size: 17px; padding: 4px; line-height: 1;
  transition: color .15s;
}
.pw-toggle:hover { color: ${C.textMid}; }

/* ── Checkbox ── */
.tf-checkbox {
  appearance: none; -webkit-appearance: none;
  width: 16px; height: 16px; border-radius: 4px;
  border: 1px solid ${C.border}; background: ${C.surfaceAlt};
  cursor: pointer; flex-shrink: 0;
  transition: border-color .15s, background .15s;
  position: relative;
}
.tf-checkbox:checked {
  background: ${C.accent}; border-color: ${C.accent};
}
.tf-checkbox:checked::after {
  content: '✓';
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%,-52%);
  font-size: 10px; color: #06070B; font-weight: 800;
}

/* ── Buttons ── */
.btn-primary {
  width: 100%; padding: 14px;
  background: ${C.accent}; color: #06070B;
  border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700;
  font-family: 'Public Sans', sans-serif;
  cursor: pointer; letter-spacing: .01em;
  transition: transform .18s, box-shadow .18s, background .18s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  position: relative; overflow: hidden;
}
.btn-primary::after {
  content:''; position:absolute; inset:0;
  background: linear-gradient(135deg,rgba(255,255,255,.16) 0%,transparent 60%);
  opacity:0; transition:opacity .2s;
}
.btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 10px 36px ${C.accentGlow}; }
.btn-primary:hover::after { opacity:1; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; transform:none; }

.btn-verify {
  padding: 11px 18px;
  background: ${C.accentDim}; color: ${C.accent};
  border: 1px solid ${C.accent}40; border-radius: 8px;
  font-size: 12px; font-weight: 700;
  font-family: 'Public Sans', sans-serif;
  cursor: pointer; white-space: nowrap;
  transition: background .2s, color .2s, border-color .2s;
  flex-shrink: 0;
}
.btn-verify:hover { background: ${C.accent}; color: #06070B; border-color: ${C.accent}; }

/* ── OTP inputs ── */
.otp-grid {
  display: grid; grid-template-columns: repeat(6,1fr); gap: 8px;
}
.otp-cell {
  aspect-ratio: 1;
  background: ${C.surfaceAlt}; border: 1px solid ${C.border};
  border-radius: 10px;
  font-family: 'IBM Plex Mono', monospace; font-size: 20px; font-weight: 500;
  color: ${C.text}; text-align: center;
  outline: none; width: 100%;
  transition: border-color .2s, box-shadow .2s;
  padding: 0;
}
.otp-cell:focus {
  border-color: ${C.accent}60;
  box-shadow: 0 0 0 3px ${C.accentDim};
}
.otp-cell.filled { border-color: ${C.accent}50; color: ${C.accent}; }
.otp-cell.otp-error { border-color: ${C.red}70; animation: shake .35s ease; }

/* ── Step dots ── */
.step-dots { display:flex; align-items:center; gap:6px; justify-content:center; }
.step-dot {
  width:7px; height:7px; border-radius:50%;
  background:${C.border}; transition:background .25s, transform .25s;
}
.step-dot.active { background:${C.accent}; transform:scale(1.3); }
.step-dot.done   { background:${C.accent}40; }

/* ── Error message ── */
.field-error {
  font-size:12px; color:${C.red};
  margin-top:5px; display:flex; align-items:center; gap:4px;
}

/* ── Success screen ── */
.success-icon {
  width:64px; height:64px; border-radius:50%;
  background:${C.accentDim}; border:1px solid ${C.accent}40;
  display:flex; align-items:center; justify-content:center;
  font-size:28px; margin:0 auto 20px;
  animation: successPop .5s cubic-bezier(.22,1,.36,1) both;
}

/* ── Spinner ── */
.spinner {
  width:18px; height:18px; border-radius:50%;
  border:2px solid rgba(255,255,255,.3);
  border-top-color:#06070B;
  animation: spinDot .7s linear infinite;
  flex-shrink:0;
}

/* ── Badge live ── */
.badge-live {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(201,161,92,.10); border:1px solid rgba(201,161,92,.28);
  border-radius:6px; padding:3px 8px;
  font-family:'IBM Plex Mono',monospace; font-size:10px; color:${C.accent};
}
.badge-live::before {
  content:''; width:5px; height:5px; border-radius:50%;
  background:${C.accent}; animation:pulse 1.5s ease-in-out infinite; flex-shrink:0;
}

/* ── Scrollbar ── */
::-webkit-scrollbar { width:5px; }
::-webkit-scrollbar-track { background:${C.bg}; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }

/* ── Language switcher ── */
.lang-switcher { position:relative; display:inline-flex; align-items:center; }
.lang-btn {
  display:inline-flex; align-items:center; gap:6px;
  background:transparent; border:1px solid ${C.border};
  border-radius:8px; padding:6px 10px;
  font-size:12px; font-weight:600; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:border-color .2s, color .2s, background .2s; white-space:nowrap;
}
.lang-btn:hover { border-color:${C.borderHover}; color:${C.text}; background:rgba(255,255,255,.04); }
.lang-dropdown {
  position:absolute; top:calc(100% + 6px); right:0;
  background:${C.surface}; border:1px solid ${C.border};
  border-radius:12px; padding:6px;
  min-width:180px; z-index:400;
  box-shadow:0 16px 40px rgba(0,0,0,.6);
  animation:fadeUp .2s ease both;
}
.lang-option {
  display:flex; align-items:center; gap:10px;
  padding:9px 12px; border-radius:8px;
  font-size:13px; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:background .15s, color .15s;
}
.lang-option:hover { background:${C.surfaceAlt}; color:${C.text}; }
.lang-option.active { color:${C.accent}; background:rgba(201,161,92,0.14); }

/* Hide Google's default toolbar */
.goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display:none !important; }
body { top:0 !important; }
.skiptranslate { display:none !important; }

/* ── Prevent Google Translate from rewriting text nodes in spots
   where React conditionally swaps DOM (step 1 ↔ step 2, validation
   errors appearing/disappearing, OTP cells). Google Translate wraps
   text nodes in <font> tags and rewrites them live; if it touches a
   node that React is about to remove/replace, React's reconciler can
   throw "Failed to execute 'removeChild' on 'Node'" because the node
   it expects to find isn't there anymore. Scoping notranslate only to
   these dynamic regions (rather than the whole page) keeps Google
   free to translate everything else — nav, footer, side panel, the
   bulk of the form labels — while protecting the conflict points. ── */
.notranslate, .notranslate * { translate: no; }

/* ── Responsive ── */
@media (max-width: 767px) {
  .side-panel-desktop { display: none !important; }
}
@media (max-width: 480px) {
  .auth-panel { padding: 20px 16px !important; }
  .otp-cell { font-size: 16px; }
  .otp-grid { gap: 6px; }
}
@media (max-width: 360px) {
  .otp-cell { font-size: 14px; border-radius: 8px; }
  .otp-grid { gap: 4px; }
}
@media (min-width: 481px) and (max-width: 767px) {
  .auth-panel { padding: 28px 32px !important; }
}
/* Ensure side panel never shrinks too small */
@media (min-width: 768px) and (max-width: 1024px) {
  .side-panel-desktop { max-width: 340px !important; }
}
`;

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER
   Reads "tf_lang" from localStorage (set by the landing page)
   and auto-applies it — preserving the user's choice across pages.
───────────────────────────────────────────── */
const LANGUAGES = [
  { code:"en",    label:"English",   flag:"🇬🇧" },
  { code:"fr",    label:"Français",  flag:"🇫🇷" },
  { code:"es",    label:"Español",   flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",   flag:"🇩🇪" },
  { code:"pt",    label:"Português", flag:"🇧🇷" },
  { code:"ar",    label:"العربية",   flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",      flag:"🇨🇳" },
  { code:"ja",    label:"日本語",    flag:"🇯🇵" },
  { code:"ru",    label:"Русский",   flag:"🇷🇺" },
  { code:"hi",    label:"हिन्दी",    flag:"🇮🇳" },
  { code:"yo",    label:"Yoruba",    flag:"🇳🇬" },
  { code:"ha",    label:"Hausa",     flag:"🇳🇬" },
  { code:"af",    label:"Afrikaans", flag:"🇿🇦" },
  { code:"pl",    label:"Polski",    flag:"🇵🇱" },
  { code:"fi",    label:"Suomi",     flag:"🇫🇮" },
  { code:"cs",    label:"Čeština",   flag:"🇨🇿" },
  { code:"ga",    label:"Gaeilge",   flag:"🇮🇪" },
  { code:"it",    label:"Italiano",  flag:"🇮🇹" },
  { code:"nl",    label:"Nederlands",flag:"🇳🇱" },
  { code:"sv",    label:"Svenska",   flag:"🇸🇪" },
  { code:"no",    label:"Norsk",     flag:"🇳🇴" },
  { code:"da",    label:"Dansk",     flag:"🇩🇰" },
  { code:"el",    label:"Ελληνικά",  flag:"🇬🇷" },
  { code:"uk",    label:"Українська",flag:"🇺🇦" },
  { code:"tr",    label:"Türkçe",    flag:"🇹🇷" },
  { code:"ko",    label:"한국어",     flag:"🇰🇷" },
  { code:"vi",    label:"Tiếng Việt",flag:"🇻🇳" },
  { code:"id",    label:"Bahasa Indonesia", flag:"🇮🇩" },
  { code:"th",    label:"ไทย",       flag:"🇹🇭" },
  { code:"sw",    label:"Kiswahili", flag:"🇰🇪" },
  { code:"he",    label:"עברית",     flag:"🇮🇱" },
  { code:"ur",    label:"اردو",      flag:"🇵🇰" },
  { code:"fa",    label:"فارسی",     flag:"🇮🇷" },
  { code:"bn",    label:"বাংলা",     flag:"🇧🇩" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage:"en", autoDisplay:false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

const LanguageSwitcher = () => {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (code) => {
    setCurrent(code);
    localStorage.setItem("tf_lang", code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} title="Change language">
        <span style={{fontSize:"14px"}}>{active.flag}</span>
        <span>{active.label}</span>
        <span style={{fontSize:"9px", opacity:.6, marginLeft:"2px"}}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div style={{fontSize:"10px", fontWeight:"600", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", padding:"4px 12px 8px"}}>
            Select Language
          </div>
          {LANGUAGES.map(l => (
            <div
              key={l.code}
              className={"lang-option" + (l.code === current ? " active" : "")}
              onClick={() => select(l.code)}
            >
              <span style={{fontSize:"16px"}}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{marginLeft:"auto", fontSize:"11px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   ICON HELPERS  (inline SVG — no icon font dep)
───────────────────────────────────────────── */
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

/* ─────────────────────────────────────────────
   FIELD with label + error
───────────────────────────────────────────── */
const Field = ({ label, hint, error, children }) => (
  <div style={{ marginBottom: "20px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "7px" }}>
      <label style={{ fontSize: "13px", fontWeight: "600", color: C.textMid, letterSpacing: ".01em" }}>{label}</label>
      {hint}
    </div>
    {children}
    {error && <div className="field-error">⚠ {error}</div>}
  </div>
);

/* ─────────────────────────────────────────────
   OTP INPUT  (6 cells, auto-advance)
───────────────────────────────────────────── */
const OTPInput = ({ value, onChange, hasError }) => {
  const refs = useRef(Array.from({ length: 6 }, () => React.createRef()));
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, -1);
      onChange(next);
      if (i > 0) refs.current[i - 1].current?.focus();
      return;
    }
    if (/^\d$/.test(e.key)) {
      const next = (value + e.key).slice(0, 6);
      onChange(next);
      if (i < 5) refs.current[i + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    refs.current[Math.min(pasted.length, 5)].current?.focus();
    e.preventDefault();
  };

  return (
    <div className="otp-grid">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs.current[i]}
          className={`otp-cell${d ? " filled" : ""}${hasError ? " otp-error" : ""}`}
          type="text" inputMode="numeric" maxLength={1}
          value={d} readOnly
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={() => refs.current[i].current?.select()}
        />
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 1 — Credentials
───────────────────────────────────────────── */
const StepCredentials = ({ onNext }) => {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow]         = useState(false);
  const [remember, setRemember] = useState(false);
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);

  const validate = () => {
    const e = {};
    if (!email.includes("@"))       e.email    = "Enter a valid email address";
    if (password.length < 6)        e.password = "Password must be at least 6 characters";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setLoading(false);
      onNext({ email });
    } catch (err) {
      setLoading(false);
      // Map Supabase error messages to friendly ones
      const msg = err.message?.toLowerCase() ?? "";
      if (msg.includes("invalid login")) {
        setErrors({ password: "Incorrect email or password." });
      } else if (msg.includes("email not confirmed")) {
        setErrors({ email: "Please verify your email before signing in." });
      } else {
        setErrors({ password: err.message || "Sign-in failed. Please try again." });
      }
    }
  };

  return (
    <div>
      <Field
        label="Email address"
        error={errors.email}
      >
        <input
          className={`tf-input${errors.email ? " error" : email.includes("@") ? " success" : ""}`}
          type="email" placeholder="alex@email.com"
          value={email} onChange={e => { setEmail(e.target.value); setErrors(p=>({...p,email:""})); }}
          onKeyDown={e => e.key==="Enter" && submit()}
          autoComplete="email"
        />
      </Field>

      <Field
        label="Password"
        hint={<a href="/forgot" style={{ fontSize:"12px", color:C.accent }}>Forgot password?</a>}
        error={errors.password}
      >
        <div className="pw-wrap">
          <input
            className={`tf-input${errors.password ? " error" : ""}`}
            type={show ? "text" : "password"}
            placeholder="Your password"
            value={password} onChange={e => { setPassword(e.target.value); setErrors(p=>({...p,password:""})); }}
            onKeyDown={e => e.key==="Enter" && submit()}
            autoComplete="current-password"
          />
          <button type="button" className="pw-toggle" onClick={() => setShow(s=>!s)} aria-label="Toggle password">
            {show ? <EyeOffIcon/> : <EyeIcon/>}
          </button>
        </div>
      </Field>

      {/* Remember me */}
      <div style={{ display:"flex", alignItems:"center", gap:"9px", marginBottom:"24px" }}>
        <input type="checkbox" className="tf-checkbox" id="remember" checked={remember} onChange={e=>setRemember(e.target.checked)}/>
        <label htmlFor="remember" style={{ fontSize:"13px", color:C.textMid, cursor:"pointer", userSelect:"none" }}>Keep me signed in for 30 days</label>
      </div>

      <button className="btn-primary" onClick={submit} disabled={loading}>
        {loading ? <><div className="spinner"/> Verifying…</> : <>Continue <span style={{fontSize:"16px"}}>→</span></>}
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 2 — Success
───────────────────────────────────────────── */
const StepSuccess = () => {
  useEffect(() => {
    const t = setTimeout(() => { window.location.href = "/dashboard"; }, 2200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{textAlign:"center", padding:"12px 0 8px"}}>
      <div className="success-icon">✓</div>
      <h3 style={{fontFamily:"'Fraunces',serif", fontSize:"20px", fontWeight:"800", color:C.text, marginBottom:"8px"}}>
        You're in!
      </h3>
      <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.65", marginBottom:"24px"}}>
        Identity verified. Redirecting you to your dashboard…
      </p>
      <div style={{display:"flex", justifyContent:"center"}}>
        <div style={{
          width:"36px", height:"36px", borderRadius:"50%",
          border:`2px solid ${C.border}`, borderTopColor:C.accent,
          animation:"spinDot .8s linear infinite",
        }}/>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   VAULT DIAL — signature element. A combination-lock
   dial rendered in brass hairlines: 60 ticks (12 major),
   a small position window, and a needle resting on the
   current mark. Reads as "the vault is open" on sign-in,
   in the same restrained, riveted-plate language as the
   landing page rather than a generic gradient blob.
───────────────────────────────────────────── */
const VaultDial = () => {
  const R = 168, CX = 200, CY = 200;
  const ticks = Array.from({ length: 60 }, (_, i) => {
    const major = i % 5 === 0;
    const angle = (i / 60) * 360 - 90;
    const rad = (angle * Math.PI) / 180;
    const rOuter = R;
    const rInner = major ? R - 16 : R - 8;
    const x1 = CX + rOuter * Math.cos(rad), y1 = CY + rOuter * Math.sin(rad);
    const x2 = CX + rInner * Math.cos(rad), y2 = CY + rInner * Math.sin(rad);
    return { x1, y1, x2, y2, major, i };
  });
  const needleAngle = -90 + (7 / 60) * 360;
  const needleRad = (needleAngle * Math.PI) / 180;
  const nx = CX + (R - 34) * Math.cos(needleRad), ny = CY + (R - 34) * Math.sin(needleRad);

  return (
    <svg width="400" height="400" viewBox="0 0 400 400" fill="none" style={{ position:"absolute", top:"-90px", right:"-110px", opacity:.9 }}>
      <circle cx={CX} cy={CY} r={R + 10} stroke={C.border} strokeWidth="1"/>
      <circle cx={CX} cy={CY} r={R - 24} stroke={C.border} strokeWidth="1"/>
      {ticks.map(t => (
        <line key={t.i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={t.i === 7 ? C.accent : C.border} strokeWidth={t.major ? 1.4 : 0.75}
          opacity={t.i === 7 ? 1 : t.major ? .8 : .45}/>
      ))}
      <line x1={CX} y1={CY} x2={nx} y2={ny} stroke={C.accent} strokeWidth="1.5" opacity=".85"/>
      <circle cx={CX} cy={CY} r="4" fill={C.accent}/>
      <circle cx={CX} cy={CY} r={R - 60} stroke={C.border} strokeWidth="1" strokeDasharray="2 4" opacity=".6"/>
    </svg>
  );
};

/* ─────────────────────────────────────────────
   SIDEBAR STATS PANEL (decorative)
───────────────────────────────────────────── */
const SidePanel = () => {
  const items = [
    { label:"Portfolio", val:"$48,291",  sub:"+28.4% this month", color:C.green },
    { label:"Copying",   val:"3 Traders", sub:"Auto-rebalanced",   color:C.blue  },
    { label:"Win Rate",  val:"76.4%",    sub:"Last 90 days",       color:C.accent},
  ];

  return (
    <div style={{
      position:"relative", flex:"1",
      background:`linear-gradient(160deg, ${C.surface} 0%, ${C.bg} 100%)`,
      borderRight:`1px solid ${C.border}`,
      padding:"48px 36px",
      display:"flex", flexDirection:"column", justifyContent:"space-between",
      overflow:"hidden", minHeight:"calc(100vh - 60px - env(safe-area-inset-top, 0px))",
    }}>
      {/* Glow behind the dial */}
      <div style={{position:"absolute", width:"340px", height:"340px", borderRadius:"50%", background:C.accentGlow, filter:"blur(110px)", opacity:.3, top:"-120px", right:"-120px", pointerEvents:"none"}}/>

      {/* Signature: vault dial */}
      <VaultDial/>

      {/* Top */}
      <div style={{position:"relative", zIndex:1}}>
        <a href="/" style={{display:"inline-flex", alignItems:"center", marginBottom:"40px"}}>
          <img src={logo} alt="Digitalbrutalnft" style={{height:"38px", width:"auto", filter:"invert(1) brightness(2)"}} />
        </a>

        <div style={{marginBottom:"12px"}}>
          <div className="badge-live" style={{marginBottom:"16px"}}>Vault Open · Markets Live</div>
          <h2 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,2.5vw,30px)", fontWeight:"600", letterSpacing:"-1px", color:C.text, lineHeight:"1.2", marginBottom:"12px"}}>
            Your portfolio,<br/><span style={{fontStyle:"italic", color:C.accent}}>never unattended.</span>
          </h2>
          <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.7", maxWidth:"280px"}}>
            Sign in to monitor live positions, review the traders you're copying, and manage every allocation.
          </p>
        </div>
      </div>

      {/* Stats cards */}
      <div style={{position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:"10px"}}>
        {items.map((it,i)=>(
          <div key={i} style={{
            background:C.surfaceAlt, border:`1px solid ${C.border}`,
            borderRadius:"2px", padding:"16px 18px",
            display:"flex", justifyContent:"space-between", alignItems:"center",
            borderLeft:`2px solid ${it.color}`,
            animation:`fadeUp .6s cubic-bezier(.22,1,.36,1) ${.4+i*.12}s both`,
          }}>
            <div>
              <div style={{fontSize:"11px", color:C.textDim, marginBottom:"4px", fontWeight:"600", letterSpacing:".06em", textTransform:"uppercase", fontFamily:"'IBM Plex Mono',monospace"}}>{it.label}</div>
              <div style={{fontFamily:"'Fraunces',serif", fontSize:"18px", color:it.color, fontWeight:"600"}}>{it.val}</div>
            </div>
            <div style={{fontSize:"11px", color:C.textDim, textAlign:"right", fontFamily:"'IBM Plex Mono',monospace"}}>{it.sub}</div>
          </div>
        ))}

        <p style={{fontSize:"11px", color:C.textDim, textAlign:"center", marginTop:"6px"}}>
          Simulated data for illustration only
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN LOGIN PAGE
───────────────────────────────────────────── */
const Login = () => {
  const [step, setStep]     = useState(1); // 1 | 2 | 3
  const [data, setData]     = useState({});

  const totalSteps = 1;

  // Inject Chatway live-chat widget
  useEffect(() => {
    if (document.getElementById("chatway-widget-script")) return;
    const script = document.createElement("script");
    script.id = "chatway-widget-script";
    script.src = "https://cdn.chatway.app/widget.js?id=1LBRSIWzKDwQ";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const goSuccess = (info) => { setData(info); setStep(2); };

  const stepLabel = { 1:"Sign in", 2:"Signed in" };

  return (
    <>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav className="tf-nav">
        <a href="/" style={{display:"flex", alignItems:"center", flexShrink:0}}>
          <img src={logo} alt="Digitalbrutalnft" style={{height:"38px", width:"auto", filter:"invert(1) brightness(2)"}} />
        </a>
        <div className="tf-nav-actions">
          <LanguageSwitcher />
          <span className="tf-nav-cta-label">New here?</span>
          <a href="/signup" className="tf-nav-cta">Sign up free →</a>
        </div>
      </nav>

      {/* ── GRID BG ── */}
      <div className="grid-bg"/>

      {/* ── LAYOUT ── */}
      <div className="tf-nav-offset" style={{
        minHeight:"calc(100vh - 60px)", display:"flex",
        position:"relative", zIndex:1,
        alignItems:"stretch",
      }}>

        {/* ── SIDE PANEL (hidden on mobile, shown ≥768px) ── */}
        <div className="side-panel-desktop" style={{display:"none", flex:"1", maxWidth:"480px"}}>
          <SidePanel/>
        </div>
        <style>{`@media(min-width:768px){ .side-panel-desktop{ display:flex !important; } }`}</style>

        {/* ── FORM PANEL ── */}
        <div style={{
          flex:"1", display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"clamp(32px,6vw,64px) clamp(16px,5vw,48px)",
        }}>
          <div style={{width:"100%", maxWidth:"400px"}}>

            {/* Header */}
            <div className="auth-header-enter" style={{marginBottom:"32px"}}>
              <h1 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(24px,3vw,30px)", fontWeight:"600", letterSpacing:"-1px", color:C.text, marginBottom:"6px", textAlign:"center"}}>
                {step===1 ? <>Welcome <span style={{fontStyle:"italic", color:C.accent}}>back</span></> : "You're in!"}
              </h1>
              <p style={{fontSize:"14px", color:C.textMid, textAlign:"center", lineHeight:"1.6"}}>
                {step===1 ? "Sign in to your Digitalbrutalnft account" : ""}
              </p>
            </div>

            {/* Card */}
            <div className="auth-card-enter notranslate" translate="no" style={{
              background:C.surface,
              border:`1px solid ${C.border}`,
              borderRadius:"10px",
              padding:"clamp(20px,4vw,32px)",
              boxShadow:`0 32px 64px rgba(0,0,0,.7), 0 0 0 1px ${C.border}`,
            }}>
              {step===1 && <StepCredentials onNext={goSuccess}/>}
              {step===2 && <StepSuccess/>}
            </div>

            {/* Footer */}
            {step === 1 && (
              <div style={{textAlign:"center", marginTop:"24px", fontSize:"13px", color:C.textDim}}>
                Don't have an account?{" "}
                <a href="/signup" style={{color:C.accent, fontWeight:"600"}}>Sign up free →</a>
              </div>
            )}

            {/* Legal */}
            <p style={{textAlign:"center", fontSize:"11px", color:C.textDim, marginTop:"20px", lineHeight:"1.6"}}>
              By signing in you agree to our{" "}
              <a href="/terms" style={{color:C.textDim, textDecoration:"underline"}}>Terms</a>
              {" "}and{" "}
              <a href="/privacy" style={{color:C.textDim, textDecoration:"underline"}}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;